import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { TranslocoPipe } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { ModalName, ModalService } from '../../../../core/services/modal.service';
import { LoyaltyService } from '../../../../core/services/loyalty.service';
import { ErrorsService } from '../../../../core/services/errors.service';
import { AuditService } from '../../../../core/services/audit.service';
import { RoundIconComponent } from '../../../../shared/round-icon/round-icon.component';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { AsyncButtonDirective } from '../../../../shared/directives/async-button.directive';
import { IconNameType } from '../../../../shared/icons/icon-name-type.enum';

const TRANSLATION_ROOT = 'modals.cancel_subscription_modal';

export interface CancelSubscriptionModalState {
  price?: number;
}

@Component({
  selector: 'cwo-cancel-subscription-modal',
  standalone: true,
  imports: [TranslocoPipe, RoundIconComponent, IconComponent, AsyncButtonDirective],
  templateUrl: './cancel-subscription-modal.component.html',
  styleUrl: './cancel-subscription-modal.component.scss',
})
export class CancelSubscriptionModalComponent implements OnInit {
  private modalService = inject(ModalService);
  private loyaltyService = inject(LoyaltyService);
  private errorsService = inject(ErrorsService);
  private auditService = inject(AuditService);

  readonly iconNameType = IconNameType;
  readonly translationRoot = TRANSLATION_ROOT;

  public loadingSubscription: Subscription | null = null;
  public allErrors: string[] = [];

  private state = computed<CancelSubscriptionModalState>(
    () => this.modalService.stateForModal(ModalName.CancelSubscription) ?? {}
  );

  private savings = signal<number | null>(null);

  readonly showSavings = computed(() => (this.savings() ?? 0) > 0);

  readonly savedAmount = computed(() => `$${(this.savings() ?? 0).toFixed(2)}`);

  readonly price = computed(() => `$${(this.state().price ?? 0).toFixed(2)}`);

  public ngOnInit(): void {
    this.loyaltyService
      .getCurrentSubscriptionSavings()
      .subscribe((savings) => this.savings.set(savings?.savings ?? null));
  }

  public closeModal(): void {
    this.modalService.closeModal(ModalName.CancelSubscription);
  }

  public cancelSubscription(): void {
    if (this.loadingSubscription) return;

    this.allErrors = [];
    this.loadingSubscription = this.loyaltyService.cancelSubscription().subscribe({
      next: () => {
        this.loadingSubscription = null;
        this.closeModal();
      },
      error: (error) => {
        this.loadingSubscription = null;
        this.setAllErrors(error);
      },
    });
  }

  private setAllErrors(error: any): void {
    const parsedError = this.errorsService.parseErrorResponse(error);
    this.auditService.createAudit(() => `Subscription Cancellation Errors: ${parsedError.allErrors}`);

    this.allErrors = parsedError.message ? [parsedError.message] : parsedError.allErrors;
  }
}
